import PropTypes from "prop-types";
import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import HotelSearchFormModal from "../../components/hotels/HotelSearchFormModal";
import MainSearchForm from "../../components/MainSearchForm";
import { usePropertyLangTag } from "../../hooks";
import { GenericCard } from "./GenericCards";
import type {
  GenericCard as GenericCardType,
  LanguageCodeWithSuffix,
  TourListInstance,
} from "./types";

const notShowProdsMobile = ["CAR", "TRF"];
export function SearchForm({ product_type, theme }) {
  const langTag: LanguageCodeWithSuffix = usePropertyLangTag();

  const [productTypeLcl, setProductTypeLcl] = useState("");
  const [mainSearchFormPosition, setMainSearchFormPosition] = useState(0);
  const [htlShowSearchModal, setHtlShowSearchModal] = useState(false);
  const [htlSearchId, setHtlSearchId] = useState<string | null>(null);
  const [htlSearchDestinationId, setHtlSearchDestinationId] = useState<string | null>(null);

  const showOnMobile = !notShowProdsMobile.includes(productTypeLcl);
  const headerTop = `calc(calc(${mainSearchFormPosition}px / 2) - 4rem)`;

  const list_page_data: TourListInstance | undefined = useSelector(
    (state: { WebsiteMetadata: { metadata: { list_pages: TourListInstance[] } } }) =>
      state.WebsiteMetadata.metadata.list_pages.find(
        (page: TourListInstance) => page.theme === theme,
      ),
  );

  useEffect(() => {
    setProductTypeLcl(product_type);
  }, []);

  const footnotes = list_page_data?.footnotes ?? [];

  const onViewHotel = useCallback(
    ({
      hotelId = null,
      destinationId = null,
    }: {
      hotelId?: string | null;
      destinationId?: string | null;
    }) => {
      if (hotelId) {
        setHtlSearchId(hotelId);
      }
      if (destinationId) {
        setHtlSearchDestinationId(destinationId);
      }
      setHtlShowSearchModal(true);
    },
    [],
  );

  // Get the language-specific generic cards, fallback to base generic_cards
  const genericCardsKey = langTag
    ? (`generic_cards${langTag}` as keyof TourListInstance)
    : "generic_cards";
  const genericCards =
    (list_page_data?.[genericCardsKey] as GenericCardType[]) ?? list_page_data?.generic_cards ?? [];

  // Group generic cards by their group attribute
  const groupedCards = genericCards.reduce(
    (acc, card) => {
      const group = card.group || "default";
      if (!acc[group]) {
        acc[group] = [];
      }
      acc[group].push(card);
      return acc;
    },
    {} as Record<string, GenericCardType[]>,
  );

  return (
    <>
      {productTypeLcl && list_page_data && (
        <div className="SearchForm relative w-screen h-[calc(100vh-8rem)] grid grid-rows-[max-content_1fr_max-content]">
          <img
            className="top-0 left-0 h-[calc(100vh-8rem)] w-screen object-cover -z-10"
            alt=""
            src={list_page_data?.images?.[0]?.photo_xl_url ?? ""}
          />
          <div
            className={`absolute left-1/2 -translate-x-1/2 w-full lg:block lg:top-[20%] ${
              showOnMobile ? "block" : "hidden"
            }`}
            style={{ top: headerTop }}
          >
            <div className="text-xl lg:text-4xl font-bold text-center text-white">
              {list_page_data?.[`title${langTag}`]}
            </div>
            <div className="text-base lg:text-xl font-medium text-center text-white">
              {list_page_data?.[`subtitle${langTag}`]}
            </div>
          </div>
          {list_page_data?.has_main_seach_bar && (
            <div className="translate-y-full">
              <MainSearchForm
                setMainSearchFormPosition={setMainSearchFormPosition}
                product_type_fixed={productTypeLcl}
                hasSingleProduct={true}
              />
            </div>
          )}
          {footnotes.length > 0 && (
            <div className="grid gap-1.5 absolute bottom-3 left-3 font-medium z-[9999] text-white">
              {footnotes.map((note, idx) => (
                <span key={idx}>{note.en}</span>
              ))}
            </div>
          )}
        </div>
      )}
      {list_page_data?.[`description${langTag}`] && (
        <div
          className="text-base lg:text-lg text-center text-gray-700 px-4 lg:px-8 py-6 lg:py-8"
          dangerouslySetInnerHTML={{
            __html: list_page_data[`description${langTag}`] ?? "",
          }}
        />
      )}
      {genericCards.length > 0 && (
        <div className="space-y-8 lg:px-[20rem]">
          {Object.entries(groupedCards).map(([group, cards]) => (
            <div key={group} className="space-y-4">
              <span className="block text-xl lg:text-2xl font-bold text-center text-gray-800">
                {group}
              </span>
              <div className="grid gap-6 lg:gap-4 xl:gap-8 justify-items-center lg:grid-cols-4">
                {cards.map((card, idx) => (
                  <GenericCard key={idx} card={card} onViewHotel={onViewHotel} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      {htlShowSearchModal && (
        <HotelSearchFormModal
          hotelId={htlSearchId}
          destinationId={htlSearchDestinationId}
          onCancel={() => {
            setHtlShowSearchModal(false);
            setHtlSearchId(null);
            setHtlSearchDestinationId(null);
          }}
        />
      )}
    </>
  );
}

SearchForm.propTypes = {
  product_type: PropTypes.string.isRequired,
  theme: PropTypes.object.isRequired,
};
